import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

import { tokenStorage } from "./tokenStorage";
import { API_ENDPOINTS } from "../constants/api";

// ─── Axios instance ──────────────────────────────────────────────────────────
const BASE_URL = process.env.REACT_APP_BASE_URL || "/api";
 
const instance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    ClientDb: process.env.REACT_APP_CLIENT_DB || "",
    ENVTYPE: `${process.env.REACT_APP_ENV}-${process.env.REACT_APP_CLIENT_DB}`,
    Accept: "*/*",
  },
  timeout: 15 * 60 * 1000,
});


// instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
//   if (!config.url) return config;

//   console.log(config.url, config.baseURL)
//   const currentUrl = new URL(config.url, config.baseURL);

//   Object.entries(config.urlParams || {}).forEach(([key, value]) => {
//     currentUrl.pathname = currentUrl.pathname.replace(
//       `:${key}`,
//       encodeURIComponent(value as string),
//     );
//   });

//   const authPart =
//     currentUrl.username && currentUrl.password
//       ? `${currentUrl.username}:${currentUrl.password}`
//       : "";

//   return {
//     ...config,
//     baseURL: config.baseURL,
//     url: currentUrl.pathname,
//   };
// });

// ─── Interceptor 1: resolve path params (:id, :slug …) ───────────────────────
instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!config.url) return config;
  let url = config.url;
 
  // Replace path params (:id, :slug, etc.)
  if (config.urlParams) {
    Object.entries(config.urlParams).forEach(([key, value]) => {
      url = url.replace(`:${key}`, encodeURIComponent(String(value)));
    });
  }
 
  return {
    ...config,
    url,
  };
});

// ─── Interceptor 2: attach Bearer token from tokenStorage ────────────────────
instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Interceptor 3: handle 401 — refresh token → retry ──────────────────────

// Queue of failed requests waiting for a token refresh to complete
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
};

function extractApiMessage(error: AxiosError): void {
  const data = error.response?.data as Record<string, unknown> | undefined;
  if (!data?.detail) return;

  if (typeof data.detail === "string") {
    error.message = data.detail;
  } else if (Array.isArray(data.detail)) {
    const msgs = data.detail
      .map((d: { msg?: string }) => d.msg)
      .filter(Boolean);
    if (msgs.length) error.message = msgs.join("; ");
  }
}

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only handle 401 once per request; skip auth endpoints to avoid loops
    if (
      error.response?.status !== 401 ||
      original._retry ||
      original.url?.includes(API_ENDPOINTS.AUTH.REFRESH) ||
      original.url?.includes(API_ENDPOINTS.AUTH.LOGIN)
    ) {
      extractApiMessage(error);
      return Promise.reject(error);
    }

    // If a refresh is already in-flight, queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return instance(original);
        })
        .catch((err) => Promise.reject(err));
    }

    original._retry = true;
    isRefreshing = true;

    const refreshToken = tokenStorage.getRefreshToken();

    if (!refreshToken) {
      isRefreshing = false;
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      return Promise.reject(error);
    }

    try {
      // Use a plain axios call (not the instance) to avoid interceptor loops
      const { data } = await axios.post(
        `${BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
        { refresh_token: refreshToken },
        {
          headers: {
            "Content-Type": "application/json",
            ClientDb: process.env.REACT_APP_CLIENT_DB || "",
          },
        },
      );

      const newAccessToken: string = data.access_token;
      const newRefreshToken: string | undefined = data.refresh_token;
      const newExpiresIn: number = data.expires_in ?? 7200;
      const newRefreshTokenExpiresIn: number | undefined =
        data.refresh_token_expires_in;

      // Persist updated tokens in cookies with correct expiry
      tokenStorage.setAccessToken(newAccessToken, newExpiresIn);
      if (newRefreshToken && newRefreshTokenExpiresIn) {
        tokenStorage.setRefreshToken(newRefreshToken, newRefreshTokenExpiresIn);
      }

      // Notify Zustand store so in-memory state stays in sync
      window.dispatchEvent(
        new CustomEvent("auth:token-refreshed", {
          detail: {
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
            expiresIn: newExpiresIn,
            refreshTokenExpiresIn: newRefreshTokenExpiresIn,
          },
        }),
      );

      processQueue(null, newAccessToken);
      isRefreshing = false;

      original.headers.Authorization = `Bearer ${newAccessToken}`;
      return instance(original);
    } catch (refreshError) {
      processQueue(refreshError, null);
      isRefreshing = false;
      tokenStorage.clearTokens();
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      return Promise.reject(refreshError);
    }
  },
);

export default instance;
